// Controles compartidos por las pestañas del workspace.
//
// Están juntos para que un filtro se vea igual en Cargas, en Líneas y en
// Facturación: tres campos de búsqueda con tres alturas distintas es lo que hace
// que una herramienta parezca cosida a trozos.
import { useTranslation } from 'react-i18next'
import { apagadoCls } from './estilos'

/** Campo de formulario de la toolbar: input, select y buscador. */
export const campoCls = (dark: boolean) =>
  [
    'h-[30px] text-[12px] px-2.5 rounded-lg border outline-none transition-colors duration-150',
    dark
      ? 'border-white/10 bg-white/[0.04] text-slate-200 placeholder:text-slate-500 focus:border-sky-500/50 hover:border-white/20'
      : 'border-slate-200 bg-white text-slate-700 placeholder:text-slate-400 focus:border-sky-400 hover:border-slate-300',
  ].join(' ')

/** Botón de un control segmentado (los grupos de líneas). */
export const segmentoCls = (dark: boolean, activo: boolean) =>
  [
    'px-2.5 h-[26px] rounded-md text-[12px] font-semibold whitespace-nowrap transition-colors duration-150 cursor-pointer',
    activo
      ? dark ? 'bg-sky-500/15 text-sky-300' : 'bg-white text-sky-700 shadow-[0_1px_2px_rgba(0,0,0,0.04)]'
      : dark ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-800',
  ].join(' ')

/** Píldora de filtro rápido (estados de carga). */
export const pildoraCls = (dark: boolean, activa: boolean) =>
  [
    'h-[30px] px-3 rounded-lg border text-[12px] font-medium transition-colors duration-150 cursor-pointer',
    activa
      ? dark
        ? 'border-sky-500/40 bg-sky-500/15 text-sky-300'
        : 'border-sky-300 bg-sky-50 text-sky-700'
      : dark
        ? 'border-white/10 text-slate-400 hover:border-white/20 hover:text-slate-200'
        : 'border-slate-200 text-slate-500 hover:border-slate-300 hover:text-slate-800',
  ].join(' ')

/** Filtro activo, con su aspa para quitarlo.
 *
 *  Existe porque un filtro que no se ve es un filtro que engaña: el usuario ve
 *  faltar filas y no sabe por qué. */
export function ChipFiltro({
  dark, etiqueta, valor, onQuitar,
}: {
  dark: boolean
  etiqueta: string
  valor: string
  onQuitar: () => void
}) {
  const { t } = useTranslation()
  return (
    <span className={`inline-flex items-center gap-1.5 h-[24px] pl-2 pr-1 rounded-md text-[11px] border ${
      dark
        ? 'border-sky-500/25 bg-sky-500/10 text-sky-200'
        : 'border-sky-200 bg-sky-50 text-sky-800'
    }`}>
      <span className={dark ? 'text-sky-400/70' : 'text-sky-600/70'}>{etiqueta}</span>
      <span className="font-semibold">{valor}</span>
      <button
        type="button"
        onClick={onQuitar}
        aria-label={`${t('common.clearFilters')}: ${etiqueta}`}
        className={`flex items-center justify-center w-4 h-4 rounded transition-colors duration-150 cursor-pointer ${
          dark ? 'hover:bg-sky-500/20' : 'hover:bg-sky-200/60'
        }`}
      >
        <svg className="w-2.5 h-2.5" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
          <path d="M2 2l6 6M8 2l-6 6" />
        </svg>
      </button>
    </span>
  )
}

/** Estado vacío discreto: una frase y, si procede, cómo salir de él.
 *
 *  Nada de ilustraciones: en una herramienta de trabajo, un dibujo grande donde
 *  debería haber datos es ruido, no consuelo. */
export function VacioDiscreto({
  dark, texto, accion,
}: {
  dark: boolean
  texto: string
  accion?: { texto: string; onClick: () => void }
}) {
  return (
    <div className={`flex flex-col items-center gap-2 py-10 text-[12px] ${apagadoCls(dark)}`}>
      <p>{texto}</p>
      {accion && (
        <button
          type="button"
          onClick={accion.onClick}
          className="text-[12px] font-semibold text-sky-600 hover:text-sky-500 cursor-pointer hover:underline transition-colors duration-150"
        >
          {accion.texto}
        </button>
      )}
    </div>
  )
}

/** Franja de cifras separadas por punto medio.
 *
 *  El mismo objeto en tres sitios —la situación del pedido en el Resumen, el
 *  totalizador de Cargas y el de Facturación— para que las tres pestañas se
 *  lean como parte del mismo sistema. Cada dato es `valor + etiqueta`, con el
 *  valor en tabulares y la etiqueta apagada detrás: así la vista salta de cifra
 *  en cifra sin leer las palabras.
 *
 *  `tono` sólo se usa cuando el dato pide una acción; el resto va en neutro. */
export interface DatoFranja {
  clave: string
  valor: string
  etiqueta: string
  tono?: 'ambar' | 'rojo'
  /** Explicación al pasar el ratón. Para un dato cuyo rótulo no cabe entero
   *  —de dónde sale la cifra, o por qué está incompleta—. */
  titulo?: string
}

export function Franja({
  dark, datos, prefijo, className = '',
}: {
  dark: boolean
  datos: DatoFranja[]
  /** Marca inicial, como la Σ de un totalizador. */
  prefijo?: string
  className?: string
}) {
  if (datos.length === 0) return null
  return (
    <div className={`flex items-baseline gap-x-1 gap-y-1 flex-wrap ${className}`}>
      {prefijo && <span className="text-sky-500 text-[12px] font-bold mr-1">{prefijo}</span>}
      {datos.map((d, i) => (
        <span key={d.clave} className="flex items-baseline gap-1.5" title={d.titulo}>
          {i > 0 && <span className={`mx-1.5 ${dark ? 'text-slate-700' : 'text-slate-300'}`}>·</span>}
          <span className={`text-[13px] font-semibold tabular-nums ${
            d.tono === 'rojo'
              ? dark ? 'text-red-300' : 'text-red-700'
              : d.tono === 'ambar'
                ? dark ? 'text-amber-300' : 'text-amber-700'
                : dark ? 'text-slate-100' : 'text-slate-900'
          }`}>
            {d.valor}
          </span>
          <span className={`text-[11px] ${
            d.tono
              ? d.tono === 'rojo'
                ? dark ? 'text-red-400/70' : 'text-red-700/70'
                : dark ? 'text-amber-400/70' : 'text-amber-700/70'
              : apagadoCls(dark)
          }`}>
            {d.etiqueta}
          </span>
        </span>
      ))}
    </div>
  )
}

/** Pie de totales de una lista, con el mismo lenguaje que el de la tabla de
 *  líneas: se queda pegado abajo para no tener que recorrer la lista entera. */
export function PieTotales({
  dark, datos,
}: {
  dark: boolean
  datos: DatoFranja[]
}) {
  return (
    <div className={`sticky bottom-0 z-10 flex items-center px-3 py-2 border-t ${
      dark ? 'border-white/15 bg-[#141d2b]' : 'border-slate-300 bg-slate-100'
    }`}>
      <Franja dark={dark} datos={datos} prefijo="Σ" />
    </div>
  )
}

/** Acción secundaria de una toolbar: presente pero sin peso de botón primario. */
export const btnSecundarioCls = (dark: boolean) =>
  [
    'h-[30px] px-2.5 rounded-lg text-[12px] font-medium transition-colors duration-150 cursor-pointer',
    dark
      ? 'text-slate-400 hover:text-slate-200 hover:bg-white/[0.06]'
      : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100',
  ].join(' ')
